import { EmployerFooter, EmployerHeader } from '@seek/adv-header-footer';
import type { Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { useToast, TextLink, Text } from 'braid-design-system';
import { type FC, useEffect, useState } from 'react';
import { useQueryParam, StringParam } from 'use-query-params';

import { ErrorContent } from 'src/App/ActionsPage/ErrorScreen/ErrorContent';
import { CandidateManagementAppSkeleton } from 'src/App/AppPageBlock/CandidateManagementAppSkeleton';
import {
  createPageAnalytics,
  type CandidatesManagementPageAnalytics,
} from 'src/App/AppPageBlock/createPageAnalytics';
import { CandidateManagementLayout } from 'src/App/CandidateManagementPage/CandidateManagement.tsx';
import AppErrorBoundary from 'src/components/app/AppErrorBoundary';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import type { HeaderFooterContextQuery } from 'src/libs/HeaderFooterContext';
import type { PageConfig } from 'src/libs/PageConfig/types';
import authServiceFactory from 'src/services/authServiceFactory';
import { environments } from 'src/siteConfig';
import { AccessType } from 'src/stores/JobStore/types';
import { getAdvertiserIds, isValidId } from 'src/utils/advertiserIds';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';
import { useStores } from 'src/utils/hooks/useStores';

import {
  CandidateManagementApp,
  type CandidateManagementAppProps,
} from '../../App/AppPageBlock';
import type { IStore } from '../../stores/Store';
import useQueryParamsStore from '../../utils/hooks/useQueryParamsStore';

import translations from './.vocab';
import * as styles from './CandidateManagement.css.ts';
import fetchInitialJobApplications from './fetchInitialJobApplications';
import { CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY } from './queryParams';
import {
  navigateToAccountSelectionPage,
  navigateToJobListPage,
  navigateToSignInPageWithReturnUrl,
} from './utils/utils';

type TranslateFn = ReturnType<typeof useTranslations>['t'];

export interface HandleEasyAccessTranslations {
  'application has been moved': ({
    firstName,
    bucket,
  }: {
    firstName: string;
    bucket: string;
  }) => string;
  close: () => string;
}

const handleEasyAccessMessages = (
  t: TranslateFn,
): HandleEasyAccessTranslations => ({
  'application has been moved': ({ firstName, bucket }) =>
    t('application has been moved', { firstName, bucket }) as string,
  close: () => t('close') as string,
});

interface Props {
  dataDogRum: PageConfig['dataDogRum'];
  pageAnalytics: CandidatesManagementPageAnalytics;
  isMobile: boolean;
  headerFooterContextData: HeaderFooterContextQuery;
  setStateAnalytics: React.Dispatch<
    React.SetStateAction<CandidatesManagementPageAnalytics>
  >;
  seekAdvertiserId: string;
}
export type JobIdType = 'jobId' | 'positionId';

interface AccessAndGetJobProps {
  store: IStore;
  jobIdType: JobIdType;
  expectedJobId: number; // either jobId or PositionId
  language: Language;
  selectedProspectId?: string;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}

const checkAccessAndFetchJobInformation = async ({
  store,
  jobIdType,
  expectedJobId,
  language,
  addCustomError,
}: AccessAndGetJobProps) => {
  const jobInfo = await store.jobStore.loadHirerJobDetailsAndAccess(
    jobIdType,
    String(expectedJobId),
    language,
    addCustomError,
  );

  if (store.jobStore.accessType === AccessType.INCORRECT_ADVERTISER) {
    navigateToAccountSelectionPage();
  }

  if (!jobInfo) {
    return { success: false } as const;
  }

  return { success: true, jobInfo } as const;
};

const CandidateManagementPage: FC<Props> = ({
  dataDogRum,
  pageAnalytics,
  isMobile,
  headerFooterContextData,
  setStateAnalytics,
  seekAdvertiserId,
}) => {
  const store = useStores();
  const [query, setQuery] = useQueryParamsStore();
  const showToast = useToast();
  const { getLabelFromBucket } = useBucketLabel();
  const { t } = useTranslations(translations);
  const messages = handleEasyAccessMessages(t);
  const [hasLoginRedirectError, setHasLoginRedirectError] = useState(false);
  const { language, site, locale, environment } = useAppConfig();
  const [initialLoadResponse, setInitialLoadResponse] = useState<
    CandidateManagementAppProps['initialLoadResponse'] | undefined
  >();
  const isStaging = environment === environments.staging;

  const [tab, setTabQueryParam] = useQueryParam(
    CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY,
    StringParam,
  );

  useEffect(() => {
    if (!initialLoadResponse) {
      const { advertiserId } = getAdvertiserIds();

      if (!isValidId(advertiserId)) {
        const authService = authServiceFactory();

        authService
          .loginWithRedirect()
          .then(() => {})
          .catch((error) => {
            dataDogRum.addCustomError({
              error,
              context: {
                activity: 'LOGIN_WITH_REDIRECTION_URL',
                description: 'Error attempting: loginWithRedirect',
              },
            });
            setHasLoginRedirectError(true);
          });
        return;
      }

      const {
        positionId: queryPositionId,
        jobId: queryJobId,
        selectedProspectId,
      } = query;
      let accessAndGetJobProps: AccessAndGetJobProps;

      if (queryPositionId) {
        accessAndGetJobProps = {
          store,
          jobIdType: 'positionId',
          expectedJobId: queryPositionId,
          language,
          selectedProspectId,
          addCustomError: dataDogRum.addCustomError,
        };
      } else if (queryJobId) {
        accessAndGetJobProps = {
          store,
          jobIdType: 'jobId',
          expectedJobId: queryJobId,
          language,
          selectedProspectId,
          addCustomError: dataDogRum.addCustomError,
        };
      } else {
        return navigateToJobListPage();
      }

      checkAccessAndFetchJobInformation(accessAndGetJobProps)
        .then((result) => {
          if (result.success) {
            const positionId = store.jobStore.job.jobInformation.positionId;
            const jobId = store.jobStore.job.jobInformation.jobId!;
            const jobInfo = result.jobInfo;

            fetchInitialJobApplications(
              store,
              (page: number | undefined) => setQuery.updatePage(page),
              advertiserId!,
              seekAdvertiserId,
              positionId,
              jobId,
              query,
              showToast,
              getLabelFromBucket,
              messages,
              language,
              dataDogRum.addCustomError,
            )
              .then(() => {
                setInitialLoadResponse({
                  ok: true,
                  jobId,
                  canBeUpgraded: jobInfo.canBeUpgraded,
                  canShowHomeLocationFilterInCandidateManagement:
                    jobInfo.canShowHomeLocationFilterInCandidateManagement,
                  canShowNationalityFilterInCandidateManagement:
                    jobInfo.canShowNationalityFilterInCandidateManagement,
                  placementV2: jobInfo.placementV2,
                });

                let userId: number | null = null;

                if (
                  headerFooterContextData?.headerFooterContext?.__typename ===
                    'HeaderFooterContext' &&
                  headerFooterContextData.headerFooterContext.user?.id
                ) {
                  const parsedUserId = Number.parseInt(
                    headerFooterContextData.headerFooterContext.user.id,
                    10,
                  );
                  if (!isNaN(parsedUserId)) {
                    userId = parsedUserId;
                  }
                }

                const getAdProductName = () => {
                  if (!jobInfo.productDisplayName) {
                    return 'Unknown';
                  }
                  if (jobInfo.billableApplicants) {
                    return 'premium pay per application';
                  }

                  return jobInfo.productDisplayName;
                };

                setStateAnalytics(
                  createPageAnalytics({
                    locale,
                    site,
                    environment,
                    userId,
                    seekAdvertiserId,
                    extraBaseAttributes: {
                      seekListingId: jobId,
                      adProductName: getAdProductName(),
                      currentPage: 'candidate list',
                      testingId: 'loyalty-beta-experience',
                    },
                  }),
                );
              })
              .catch((error) => {
                if (error.message === 'UNAUTHENTICATED') {
                  navigateToSignInPageWithReturnUrl();
                  setInitialLoadResponse({ ok: false });
                  return;
                }
                dataDogRum.addCustomError({
                  error,
                  context: {
                    activity: 'FETCH_INITIAL_JOB_APPLICATIONS',
                    description:
                      'Error attempting: fetchInitialJobApplications',
                  },
                });
                setInitialLoadResponse({ ok: false });
              });
          } else {
            setInitialLoadResponse({ ok: false });
          }
        })
        .catch((error) => {
          dataDogRum.addCustomError({
            error,
            context: {
              activity: 'FETCH_ACCESS_AND_JOB_INFORMATION',
              description: 'Error attempting: fetchInitialJobApplications',
            },
          });
          store.jobStore.setIsError();
          setInitialLoadResponse({ ok: false });
        });
    }
  }, [
    query.positionId,
    query.bucket,
    query.page,
    query.selectedProspectId,
    store,
    query.search,
    query,
    setQuery,
    showToast,
    getLabelFromBucket,
    messages,
    setTabQueryParam,
    tab,
    language,
    site,
    initialLoadResponse,
    environment,
    headerFooterContextData,
    locale,
    setStateAnalytics,
    seekAdvertiserId,
    dataDogRum,
  ]);

  useEffect(() => {
    if (query.referrer === 'edit-page-success') {
      showToast({
        message: t('Your job has been updated'),
        tone: 'positive',
        closeLabel: t('close'),
      });
      setQuery.clearReferrer();
    }
  }, [showToast, query.referrer, setQuery, t]);

  const loadedEmployerHeader = (
    <div className={styles.header}>
      <EmployerHeader
        activeTabId="jobs"
        site={site}
        locale={locale}
        isStaging={isStaging}
        identityContext={
          headerFooterContextData.headerFooterContext?.identityContext
        }
        advertiser={headerFooterContextData.headerFooterContext?.advertiser}
        user={headerFooterContextData.headerFooterContext?.user}
        data-testid="employer-header"
        divider
        onTrackedItemInteraction={(event) => {
          pageAnalytics.adapter.link({
            event: event.eventName,
            attributes: { ...event },
          });
        }}
      />
    </div>
  );

  if (hasLoginRedirectError) {
    return (
      <CandidateManagementLayout
        header={isMobile ? null : loadedEmployerHeader}
        content={
          <ErrorContent
            errorCode="500"
            heading={t('Can we try that again?')}
            body={
              <Text tone="secondary">
                {t("Retry the page to see your candidate's profile", {
                  Link: (children) => (
                    <TextLink href={window.location.href}>{children}</TextLink>
                  ),
                })}
              </Text>
            }
          />
        }
        footer={
          <EmployerFooter
            site={site}
            locale={locale}
            isStaging={isStaging}
            identityContext={
              headerFooterContextData.headerFooterContext?.identityContext
            }
            advertiser={headerFooterContextData.headerFooterContext?.advertiser}
          />
        }
      />
    );
  }

  if (!initialLoadResponse) {
    return (
      <CandidateManagementLayout
        header={isMobile ? null : loadedEmployerHeader}
        content={
          <AppErrorBoundary>
            <CandidateManagementAppSkeleton />
          </AppErrorBoundary>
        }
        footer={
          <EmployerFooter
            site={site}
            locale={locale}
            isStaging={isStaging}
            identityContext={
              headerFooterContextData.headerFooterContext?.identityContext
            }
            advertiser={headerFooterContextData.headerFooterContext?.advertiser}
          />
        }
      />
    );
  }

  return (
    <CandidateManagementLayout
      header={isMobile ? null : loadedEmployerHeader}
      content={
        <AppErrorBoundary>
          <CandidateManagementApp
            employerHeader={loadedEmployerHeader}
            initialLoadResponse={initialLoadResponse}
            pageAnalytics={pageAnalytics}
            dataDogRum={dataDogRum}
            setInitialLoadResponse={setInitialLoadResponse}
          />
        </AppErrorBoundary>
      }
      footer={
        <EmployerFooter
          site={site}
          locale={locale}
          isStaging={isStaging}
          identityContext={
            headerFooterContextData.headerFooterContext?.identityContext
          }
          advertiser={headerFooterContextData.headerFooterContext?.advertiser}
        />
      }
    />
  );
};

export default CandidateManagementPage;
